import { createContext, createElement, use, useSyncExternalStore, type ReactNode } from 'react';

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
};

type WindowWithPwaInstallListener = Window & {
  __viraTempoPwaInstallListener?: true;
};

type InstallStateSnapshot = {
  promptEvent: BeforeInstallPromptEvent | null;
  isInstalled: boolean;
};

let storedPromptEvent: BeforeInstallPromptEvent | null = null;
let storedIsInstalled = false;
const listeners = new Set<() => void>();
let installStateSnapshot: InstallStateSnapshot = {
  promptEvent: storedPromptEvent,
  isInstalled: storedIsInstalled,
};

function notifyListeners() {
  listeners.forEach((listener) => listener());
}

function isRunningStandalone() {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }

  const standaloneNavigator = navigator as Navigator & { standalone?: boolean };

  return window.matchMedia('(display-mode: standalone)').matches || standaloneNavigator.standalone === true;
}

type PwaInstallContextValue = {
  canInstall: boolean;
  install: () => Promise<boolean>;
  isInstalled: boolean;
};

const PwaInstallContext = createContext<PwaInstallContextValue | null>(null);

function refreshInstallStateSnapshot() {
  installStateSnapshot = {
    promptEvent: storedPromptEvent,
    isInstalled: storedIsInstalled || isRunningStandalone(),
  };
}

function getInstallStateSnapshot() {
  return installStateSnapshot;
}

function subscribeToInstallState(listener: () => void) {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
}

function registerGlobalInstallListeners() {
  if (typeof window === 'undefined') {
    return;
  }

  const pwaWindow = window as WindowWithPwaInstallListener;

  if (pwaWindow.__viraTempoPwaInstallListener) {
    return;
  }

  pwaWindow.__viraTempoPwaInstallListener = true;

  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();

    storedPromptEvent = event as BeforeInstallPromptEvent;
    storedIsInstalled = false;
    refreshInstallStateSnapshot();
    notifyListeners();
  });

  window.addEventListener('appinstalled', () => {
    storedPromptEvent = null;
    storedIsInstalled = true;
    refreshInstallStateSnapshot();
    notifyListeners();
  });
}

registerGlobalInstallListeners();

async function installFromPrompt(promptEvent: BeforeInstallPromptEvent | null) {
  if (!promptEvent) {
    return false;
  }

  await promptEvent.prompt();
  const choice = await promptEvent.userChoice.catch(() => null);

  storedPromptEvent = null;
  refreshInstallStateSnapshot();
  notifyListeners();

  return choice?.outcome === 'accepted';
}

export function PwaInstallProvider({ children }: { children: ReactNode }) {
  refreshInstallStateSnapshot();
  const { promptEvent, isInstalled } = useSyncExternalStore(
    subscribeToInstallState,
    getInstallStateSnapshot,
    getInstallStateSnapshot,
  );

  const install = () => installFromPrompt(promptEvent);

  const value = {
    canInstall: Boolean(promptEvent) && !isInstalled,
    install,
    isInstalled,
  };

  return createElement(PwaInstallContext.Provider, { value }, children);
}

export function usePwaInstall() {
  const context = use(PwaInstallContext);

  if (!context) {
    throw new Error('usePwaInstall must be used inside PwaInstallProvider');
  }

  return context;
}

export function __resetPwaInstallStateForTests() {
  storedPromptEvent = null;
  storedIsInstalled = false;
  refreshInstallStateSnapshot();
  notifyListeners();
}
